import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { removeLoader, showLoader, showToast, TOAST_TYPE } from "deskera-ui-library";
import { TABLE_DISPLAY_NAME, TABLES } from "../../managers/TableManger";
import RouteManager from "../../managers/RouteManager";
import AppManager from "../../managers/AppManager";
import DataGridHolderPayroll from "../common/DataGridHolderPayroll";
import PermissionUtils, { ACTION, MODULE } from "../user-permissions/PermissionUtils";
import PolicyAndResourcesService from "./PolicyAndResourcesService";
import {
	fetchPolicyAndResourceDocuments,
	selectPolicyAndResourceDocuments,
} from "../../Redux/Slices/PolicyAndResourcesSlice";
import AddNewDocument from "./AddNewDocument";
import AttachmentService from "../../services/AttachmentService";
import { saveAs } from 'file-saver';
import { getFileName } from "../configs/config";
import { TENANT, TenantService } from "../../managers/TenantManagerService";

const PolicyAndResources = (props) => {
	const isAdmin = TenantService.get(TENANT.IS_ADMIN);

	const history = useHistory();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const documents = useSelector(selectPolicyAndResourceDocuments);

	const [showAddNewDocument, setShowAddNewDocument] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPageCount, setTotalPageCount] = useState(1);
	const [gridData, setGridData] = useState({
		columnData: PolicyAndResourcesService.COLUMN_CONFIG,
		rowData: [],
		filter: [],
		originalData: [],
	});
	const [permission, setPermission] = useState(null);

	useEffect(() => {
		RouteManager.setPresenter({ props: { ...props, history: history } });
		AppManager.scrollToTop();

		setPermission({
			CREATE: PermissionUtils.hasAccess(MODULE.POLICY_AND_RESOURCES, ACTION.CREATE),
			DELETE: PermissionUtils.hasAccess(MODULE.POLICY_AND_RESOURCES, ACTION.DELETE)
		});

		getPolicyAndResourceDocuments(PolicyAndResourcesService.API_CONFIG);
	}, []);

	useEffect(() => {
		if (documents) {
			const { filterConfig, tenantDocument } = documents;
			if (filterConfig) {
				setTotalPageCount(filterConfig.totalPage);
			}
	
			if (Array.isArray(tenantDocument)) {
				const rowData = getRowData(tenantDocument);
	
				setGridData({
					...gridData,
					rowData: rowData,
					originalData: rowData,
				});
			} else {
				setGridData({
					...gridData,
					rowData: [],
					originalData: [],
				});
			}
		}
	}, [documents]);

	const getPolicyAndResourceDocuments = async (apiConfig) => {
		showLoader();
		PolicyAndResourcesService.API_CONFIG = apiConfig;
		await dispatch(fetchPolicyAndResourceDocuments());
		removeLoader();
	};

	const getRowData = (documents) => {
		const rowData = [];
		if (documents && documents.length > 0) {
			documents.forEach((document) => {
				const row = {
					...document,
					name: document.name,
					description: document.description,
				};

				rowData.push(row);
			});
		}

		return rowData;
	};

	const onRowDelete = (data) => {
		const ids = [];
		if (Array.isArray(data)) {
			data.forEach((document) => {
				ids.push(document.id);
			});
		} else if (data.rowData) {
			ids.push(data.rowData.id);
		}

		if (ids.length === 0) {
			return;
		}

		showLoader("Deleting document/s... Please wait");
		const payload = {
			ids: ids,
		};

		PolicyAndResourcesService.deletePolicyAndResourceDocument(payload).then(
			(response) => {
				getPolicyAndResourceDocuments(PolicyAndResourcesService.API_CONFIG);
				removeLoader();
				showToast("Document/s deleted successfully.", TOAST_TYPE.SUCCESS);
			},
			(error) => {
				removeLoader();
				const message = error?.message || t("SOMETHING_WENT_WRONG");
				showToast(message, TOAST_TYPE.FAILURE);
			}
		);
	};

	const onView = (data) => {
		if (data?.rowData) {
			showLoader("Downloading document... Please wait");
			AttachmentService.getPresignedURL(data.rowData.path).then(
				(response) => {
					AttachmentService.downloadFile(response).then(
						(response) => {
							removeLoader();
							saveAs(response, getFileName(data.rowData.path));
						},
						(error) => {
							removeLoader();
						}
					);
				},
				(error) => {
					removeLoader();
				}
			);
		}
	};

	const onSort = (data) => {
		if (data?.columnData) {
			const apiConfig = {
				...PolicyAndResourcesService.API_CONFIG,
				field: data.columnData.columnCode,
				order: String(data.order).toLowerCase(),
			}
			getPolicyAndResourceDocuments(apiConfig);
		}
	}

	return (
		<Fragment>
			{showAddNewDocument && (
				<AddNewDocument
					onClose={(refresh) => {
						setShowAddNewDocument(false);
						if (refresh) {
							getPolicyAndResourceDocuments(PolicyAndResourcesService.API_CONFIG);
						}
					}}
				/>
			)}
			<div className="parent-width parent-height">
				<DataGridHolderPayroll
					tableName={TABLES.POLICY_AND_RESOURCES}
					displayTableName={TABLE_DISPLAY_NAME[TABLES.POLICY_AND_RESOURCES]}
					allowBulkOperation={isAdmin}
					allowColumnEdit={false}
					allowColumnDelete={false}
					allowColumnShift={false}
					allowSearch={false}
					allowFilter={false}
					allowColumnAdd={false}
					allowRowAdd={false}
					allowColumnSort={true}
					refresh={false}
					allowDataExport={false}
					allowRowEdit={false}
					showContextEdit={false}
					contextMenu={isAdmin}
					isMenuVisible={true}
					showContextMenu={true}
					isActionEnabled={true}
					onRowOpenBtnHidden={true}
					gridData={gridData}
					currentPage={currentPage}
					totalPageCount={totalPageCount}
					onRowEdit={() => {}}
					onRowDelete={onRowDelete}
					onView={onView}
					onPaginationClick={(requestedPageNumber) => {
						setCurrentPage(requestedPageNumber);
						const apiConfig = {
							...PolicyAndResourcesService.API_CONFIG,
							currentPage: requestedPageNumber
						}
						getPolicyAndResourceDocuments(apiConfig);
					}}
					addButtonConfig={permission?.CREATE ? [
						{
							title: `+ ${"Add New Document"}`,
							className: "bg-app text-white ml-s",
							onClick: () => {
								setShowAddNewDocument(true);
							},
						},
					] : []}
					addMenuButton={permission?.DELETE ? [
						{
							title: "Delete",
							className: "border-red mr-r bg-red text-white",
							onClick: onRowDelete
						}
					] : []}
					onSort={onSort}
					module={MODULE.POLICY_AND_RESOURCES}
				/>
			</div>
		</Fragment>
	);
};

export default PolicyAndResources;
